
import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSubscription } from '@/hooks/useSubscription';
import { Loader2 } from 'lucide-react';
import { useAppSelector } from '@/hooks/useAppSelector';
import { SubscriptionModal } from '@/components/subscription/SubscriptionModal';

interface RequireSubscriptionProps {
  children: ReactNode;
  feature?: 'number_of_queries_per_day' | 'can_view_dashboard' | 'can_view_competitors_dashboard';
}

export function RequireSubscription({ children, feature }: RequireSubscriptionProps) {
  const location = useLocation();
  const subscription = useSubscription();
  const { loading } = useAppSelector((state) => state.auth);

  const checkAccess = () => {
    switch (feature) {
      case 'number_of_queries_per_day':
        return subscription.hasQueryCapacity();
      case 'can_view_dashboard':
        return subscription.canViewDashboard();
      case 'can_view_competitors_dashboard':
        return subscription.canViewCompetitorsDashboard();
      default:
        return true;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!checkAccess()) {
    return (
      <>
        <Navigate to="/" state={{ from: location }} replace />
        <SubscriptionModal
          isOpen={subscription.isModalOpen}
          onClose={subscription.onCloseModal}
          feature={subscription.modalConfig.feature}
          reason={subscription.modalConfig.reason}
        />
      </>
    );
  }

  return (
    <>
      {children}
      <SubscriptionModal
        isOpen={subscription.isModalOpen}
        onClose={subscription.onCloseModal}
        feature={subscription.modalConfig.feature}
        reason={subscription.modalConfig.reason}
      />
    </>
  );
}
